import type {
  AdjustPicklistItemRequest,
  BinInfoDto,
  PicklistItemGroupDto,
  PicklistPlannedLineDto,
  TakenByDto,
} from '../types';
import { PicklistAdjustmentReason, ReceivingItemType, SerialDisposition } from '../types';
import { adjustRowError } from './adjustCaps';
import { qty, unitWord } from './picklistProgress';

/**
 * PT-6 — the Adjust dialog's pure layer: cards and rows from ONE item group, the balance, the dispositions and every
 * settled string (`o20/PT-6_full_file_settlement.md`). No React, no fetch; `adjustModel.test.ts` pins it.
 *
 * PT-6b — a Move is no longer staged here. It is its own dialog and its own request (`moveModel.ts`); this model only
 * says whether a row's move control is offered, and why it is disabled while Adjust holds unapplied edits.
 *
 * **A row only goes DOWN here (owner, 2026-10-05).** Lowering takes units off the pick; counting units for another
 * MRS line is a Move, and nothing else. A row used to rise into the room its bin's other lines gave up, which made
 * "lower A, raise B" a second, unlabelled way to re-allocate. That is gone, and with it the over-the-bin state and the
 * "this row can go up to" figure. The server refuses a rise too, in the same words (`adjustCaps.ts`, ISS-R-011, F-02).
 *
 * **A card is a display bin** of the group (`bins[]`); a serialized item's display bins group into one card per live
 * bin. **A row is one entry of a display bin's `lines[]`**: a line and its own slot there (C1). A display bin with
 * nothing picked is one locked row with no MRS chip. The provisional split (`allocatedQuantity`) is never read.
 *
 * Every card carries a `sig`, a string of everything it renders, so a card whose figures did not change can skip its
 * re-render (the React Compiler cannot see through derived objects that are rebuilt on every keystroke).
 */

// ── The line label (C5), mirroring CSC_CORE `MrsLineLabel` (PT-2's one rule) ───────────────────────────────

/** Trimmed, every whitespace run collapsed to one space. */
export const normaliseLabelText = (text?: string | null): string => (text ?? '').trim().replace(/\s+/g, ' ');

/**
 * A line's label: its MRS number, or `{mrs} — {remarks}` when the item has another planned line on the same MRS
 * (compared by `materialRequestId`, never the number). O-40: identical remarks add ` · {end use}`; blank remarks read
 * as the end use alone. Never a "line N" ordinal (D4). Nothing is truncated.
 */
export function mrsLineLabel(line: PicklistPlannedLineDto, groupLines: readonly PicklistPlannedLineDto[]): string {
  const siblings = groupLines.filter((l) => l.materialRequestId === line.materialRequestId && l.id !== line.id);
  if (siblings.length === 0) return line.mrsNumber;

  const remarks = normaliseLabelText(line.remarks);
  const endUse = normaliseLabelText(line.netsuiteEndUseName);
  if (!remarks) return endUse ? `${line.mrsNumber} — ${endUse}` : line.mrsNumber;

  const identical = siblings.some((s) => normaliseLabelText(s.remarks).toLowerCase() === remarks.toLowerCase());
  return identical && endUse ? `${line.mrsNumber} — ${remarks} · ${endUse}` : `${line.mrsNumber} — ${remarks}`;
}

/** The part of a label after " — ", or empty for a bare MRS number. */
export const labelTail = (label: string): string => {
  const dash = label.indexOf(' — ');
  return dash < 0 ? '' : label.slice(dash + 3);
};

// ── The form ────────────────────────────────────────────────────────────────────────────────────────────────

export interface SerialRowState {
  mode: 'return';
  disp?: SerialDisposition;
}

/** What the person has typed or chosen. The dialog's store holds it; the model reads it. */
export interface AdjustForm {
  reason: PicklistAdjustmentReason;
  notes: string;
  /** Quantity boxes as typed, by row key. */
  vals: Readonly<Record<string, string>>;
  /** Serial rows: absent = Keep. */
  ser: Readonly<Record<string, SerialRowState>>;
  /** Where each display bin's unassigned units went (quantity items), by display bin id. */
  disp: Readonly<Record<number, SerialDisposition>>;
}

export const emptyAdjustForm = (): AdjustForm => ({
  // The default is Wrong quantity (the prototype's `reason:'WrongQuantity'`, and today's).
  reason: PicklistAdjustmentReason.WrongQuantity,
  notes: '',
  vals: {},
  ser: {},
  disp: {},
});

/** The form after a 409 reload: every number dropped, the reason and the note kept (the prototype's reset). */
export const staleResetForm = (form: AdjustForm): AdjustForm => ({
  ...emptyAdjustForm(),
  reason: form.reason,
  notes: form.notes,
});

// ── The model ───────────────────────────────────────────────────────────────────────────────────────────────

export interface BreakdownModel {
  title: string;
  sub: string;
  rows: { bin: string; value: string; was: string; isThis: boolean }[];
  sumLabel: string;
  over: boolean;
}

export interface DispositionModel {
  question: string;
  value: SerialDisposition | null;
  /** 'bin': the card's unassigned units (keyed by display bin id); 'serial': this serial's unpick. */
  target: 'bin' | 'serial';
  flagText: string;
  incomplete: boolean;
}

export interface AdjustRowModel {
  key: string;
  lineId: number | null;
  slotId: number | null;
  displayBinId: number;
  kind: 'qty' | 'serial' | 'locked' | 'taken';
  /** The row's picked units now: typing it back into the box clears the edit, and nothing above it is accepted. */
  current: number;
  /** Its posted units: the lowest the box may go. */
  floor: number;
  serial: string | null;
  /** The MRS chip (a plain label, not a hover card). */
  mrsNumber: string | null;
  /** The line label's tail, when the item has two or more lines on that MRS. */
  mrsSub: string;
  /** A one-line, non-serial item shows "Picked quantity" in place of the chips. */
  plain: boolean;
  curLabel: string;
  /**
   * On a PARTLY posted row only (owner, 2026-10-05): what can still change, then the posted floor. Without it the
   * row reads "current 20" and nothing says 8 of them are already issued. No slip number; a fully posted row is
   * `lockedLabel` instead, and an unposted row carries nothing.
   */
  postedNote: { open: string; posted: string; tip: string } | null;
  planLabel: string;
  planTip: string;
  lockedLabel: string;
  takenLabel: string;
  value: string;
  error: string | null;
  /** PT-6b — the quantity row's move icon is offered (it opens the Move dialog). */
  canMoveQty: boolean;
  /** PT-6b — the serial segment's "Move…" is offered. */
  canMoveSer: boolean;
  /** The move control's tooltip and accessible name. */
  moveTip: string;
  /** True when the control is shown but cannot be used now (`moveTip` says why). */
  moveDisabled: boolean;
  serMode: 'keep' | 'return';
  disposition: DispositionModel | null;
  breakdown: BreakdownModel | null;
}

export interface AdjustCardSum {
  pill: string;
  tone: 'warning' | 'success';
  icon: 'circle-dashed' | 'circle-check' | 'circle-minus';
  meta: string;
  /** The pill's hover card (owner, 2026-09-30): the bin's figures and what Adjust can and cannot do with them. */
  tip: AdjustSumTip;
}

/**
 * The mental model, on the pill. Adjust corrects a RECORDED pick: it lowers a line (the units come off the pick), and
 * the move icon counts units for another MRS line. It never adds units — picking more is done on the handheld.
 */
export interface AdjustSumTip {
  bin: string;
  state: string;
  figures: { label: string; value: string; tone: 'warning' | null }[];
  can: string[];
  cannot: string;
  /** What to do now; empty when the bin is balanced. */
  next: string;
}

const CANNOT_ADD = 'Add units — picking more is done on the handheld.';

export interface AdjustCardModel {
  key: string;
  bin: string;
  meta: string;
  sum: AdjustCardSum | null;
  rows: AdjustRowModel[];
  /** Everything the card renders, as one string: a card whose sig did not change need not re-render. */
  sig: string;
}

export type AdjustPlanBody = Pick<AdjustPicklistItemRequest, 'rows' | 'moves' | 'dispositions'>;

export interface AdjustModel {
  code: string;
  name: string;
  unit: string;
  isSerial: boolean;
  multi: boolean;
  cards: AdjustCardModel[];
  bannerTail: string;
  hintText: string;
  showHint: boolean;
  bulkShow: boolean;
  bulkSummary: string;
  returnAllKeys: string[];
  qtyReset: boolean;
  needNote: boolean;
  canApply: boolean;
  /** What Apply sends (the version and the header fields are added by `buildAdjustRequest`). */
  plan: AdjustPlanBody;
}

interface Base {
  key: string;
  lineId: number | null;
  slotId: number | null;
  display: BinInfoDto;
  current: number;
  posted: number;
  split: boolean;
  taken: TakenByDto | null;
  serial: string | null;
}

const DISPOSITION_FLAG = (bin: string) => `They still count in ${bin} in NetSuite until the stock is adjusted there.`;

/** The serial-tracked rule (PicklistItemGroups.IsSerialTracked): Serialized, or unknown with a serial on any bin. */
export const isSerialGroup = (group: PicklistItemGroupDto): boolean =>
  group.itemType === ReceivingItemType.Serialized
  || (group.itemType == null && (group.bins ?? []).some((b) => b.inventoryNumberId != null));

/** C1 — a group snapshot's rows, per display bin in `bins` order, entries in planned-line order. */
function baseRows(group: PicklistItemGroupDto, isSerial: boolean): Base[] {
  const order = new Map((group.plannedLines ?? []).map((l, i) => [l.id, i] as const));
  const out: Base[] = [];
  for (const display of group.bins ?? []) {
    const entries = [...(display.lines ?? [])].sort(
      (a, b) => (order.get(a.picklistItemId) ?? 0) - (order.get(b.picklistItemId) ?? 0)
        || a.picklistItemBinId - b.picklistItemBinId,
    );
    const split = new Set(entries.map((e) => e.picklistItemId)).size > 1;
    const serial = isSerial ? display.inventoryNumber ?? null : null;
    if (entries.length === 0) {
      out.push({
        key: `bin:${display.id}`, lineId: null, slotId: null, display, current: 0, posted: 0, split: false,
        taken: display.takenBy ?? null, serial,
      });
      continue;
    }
    for (const e of entries) {
      out.push({
        key: `${e.picklistItemId}:${e.picklistItemBinId}`, lineId: e.picklistItemId, slotId: e.picklistItemBinId,
        display, current: e.currentlyPickedQuantity, posted: e.postedQuantity, split, taken: null, serial,
      });
    }
  }
  return out;
}

export function buildAdjustModel(group: PicklistItemGroupDto, form: AdjustForm): AdjustModel {
  const plannedLines = group.plannedLines ?? [];
  const lineById = new Map(plannedLines.map((l) => [l.id, l] as const));
  const unit = group.unitOfMeasure ?? '';
  const uw = (n: number) => unitWord(unit, n);
  const isSerial = isSerialGroup(group);
  const multi = plannedLines.length > 1;
  const label = (id: number) => {
    const line = lineById.get(id);
    return line ? mrsLineLabel(line, plannedLines) : '';
  };
  const hasSiblingOnMrs = (id: number) => {
    const line = lineById.get(id);
    return !!line && plannedLines.filter((l) => l.materialRequestId === line.materialRequestId).length > 1;
  };
  const planned = (id: number) => lineById.get(id)?.quantityPlanned ?? 0;

  const bases = baseRows(group, isSerial);

  const text = (b: Base) => form.vals[b.key] ?? String(b.current);
  const num = (b: Base) => {
    const t = text(b);
    return t.trim() === '' ? Number.NaN : Number(t);
  };
  const liveValue = (b: Base) => {
    const n = num(b);
    return Number.isFinite(n) ? n : b.current;
  };
  /** What the row will hold if Apply is pressed now: the typed figure when it is a valid lowering, else unchanged. */
  const settled = (b: Base) => {
    const n = num(b);
    return Number.isFinite(n) && n >= b.posted && n <= b.current ? n : b.current;
  };

  const rowsOfLine = (id: number) => bases.filter((b) => b.lineId === id);
  const pickedOfLine = (id: number) => rowsOfLine(id).reduce((a, b) => a + b.current, 0);
  const room = (id: number) => Math.max(0, planned(id) - pickedOfLine(id));
  const otherRowsValue = (b: Base) =>
    b.lineId == null ? 0 : rowsOfLine(b.lineId).filter((o) => o.key !== b.key).reduce((a, o) => a + liveValue(o), 0);

  // PT-6b — a Move saves at once and re-seeds this form, so it is not offered over unapplied edits.
  const dirty = Object.keys(form.vals).length > 0 || Object.keys(form.ser).length > 0 || Object.keys(form.disp).length > 0;

  // Per display bin (quantity items): the unassigned units, the pill and the disposition's home (its last row).
  const binState = new Map<number, { pk: number; decrease: number; sum: AdjustCardSum | null }>();
  if (!isSerial) {
    for (const display of group.bins ?? []) {
      const rows = bases.filter((b) => b.display === display);
      const withLine = rows.filter((b) => b.lineId != null);
      if (withLine.length === 0) continue;
      const pk = withLine.reduce((a, b) => a + b.current, 0);
      if (rows[0].split) {
        const asg = withLine.reduce((a, b) => a + settled(b), 0);
        const sb = Math.max(pk - asg, 0);
        const bin = display.binNumber;
        binState.set(display.id, {
          pk, decrease: sb,
          sum: {
            pill: sb > 0 ? `${qty(sb)} ${uw(sb)} unassigned` : 'All assigned',
            tone: sb > 0 ? 'warning' : 'success',
            icon: sb > 0 ? 'circle-dashed' : 'circle-check',
            meta: `on MRS lines ${qty(asg)} of ${qty(pk)}`,
            tip: {
              bin,
              state: sb > 0 ? `${qty(sb)} ${uw(sb)} unassigned` : 'All assigned',
              figures: [
                { label: `Picked in ${bin}`, value: `${qty(pk)} ${uw(pk)}`, tone: null },
                { label: 'On MRS lines now', value: `${qty(asg)} ${uw(asg)}`, tone: sb > 0 ? 'warning' : null },
              ],
              can: ['Lower a line — the units come off the pick', 'Count units for another MRS line — with the move icon'],
              cannot: CANNOT_ADD,
              next: sb > 0 ? 'Say below where the lowered units went.' : '',
            },
          },
        });
      } else {
        const only = withLine[0];
        const nv0 = settled(only);
        const off = Math.max(pk - nv0, 0);
        binState.set(display.id, {
          pk, decrease: off,
          sum: off > 0
            ? {
                pill: `${qty(off)} ${uw(off)} unassigned`,
                tone: 'warning',
                icon: 'circle-minus',
                meta: `${multi ? 'on MRS line ' : 'now '}${qty(nv0)} of ${qty(pk)}`,
                tip: {
                  bin: display.binNumber,
                  state: `${qty(off)} ${uw(off)} unassigned`,
                  figures: [
                    { label: `Picked in ${display.binNumber}`, value: `${qty(pk)} ${uw(pk)}`, tone: null },
                    { label: 'After this change', value: `${qty(nv0)} ${uw(nv0)}`, tone: 'warning' },
                  ],
                  can: ['Lower the pick — the units come off it'],
                  cannot: CANNOT_ADD,
                  next: 'Say below where the lowered units went.',
                },
              }
            : null,
        });
      }
    }
  }

  const lastKeyOfDisplay = new Map<number, string>();
  for (const b of bases) lastKeyOfDisplay.set(b.display.id, b.key);

  const rowModels = bases.map((b): AdjustRowModel => {
    const taken = !!b.taken;
    const P = b.lineId != null ? planned(b.lineId) : 0;
    const others = otherRowsValue(b);
    // Nothing left to lower: not picked, or every unit posted.
    const locked = !taken && (b.lineId == null || b.current <= b.posted);
    const kind: AdjustRowModel['kind'] = taken ? 'taken' : locked ? 'locked' : isSerial ? 'serial' : 'qty';
    const lineRows = b.lineId != null ? rowsOfLine(b.lineId) : [];

    let error: string | null = null;
    if (kind === 'qty') {
      const n = num(b);
      // ISS-R-011 — a row never rises; the server's check, in the server's words.
      error = !Number.isFinite(n)
        ? 'Enter a quantity.'
        : adjustRowError({ current: b.current, posted: b.posted, value: n, itemHasSeveralLines: multi, unit });
    }

    // The planned label and its breakdown card (C1, scope 5; settlement rows 19, 20, 25–28).
    let planLabel = '';
    let planTip = '';
    let breakdown: BreakdownModel | null = null;
    if (b.lineId != null && !(isSerial && plannedLines.length === 1)) {
      const shared = lineRows.length;
      const otherBins = [...new Set(lineRows.filter((o) => o.key !== b.key).map((o) => o.display.binNumber))];
      const where = otherBins.length === 1 ? otherBins[0] : `${otherBins.length} other bins`;
      planLabel = shared > 1 ? `${qty(P)} planned in total` : `planned ${qty(P)}`;
      planTip = `${label(b.lineId)} is planned for ${qty(P)} ${uw(P)} on this picklist`
        + (shared > 1
          ? `, across all bins. ${qty(others)} ${uw(others)} are on it in ${where}.`
          : ' — the most that can be picked for it.');

      const same = multi ? lineRows : bases.filter((o) => !o.taken && o.lineId != null);
      const sum = same.reduce((a, o) => a + liveValue(o), 0);
      const over = Math.max(sum - P, 0);
      breakdown = {
        title: label(b.lineId),
        sub: `Planned on this picklist · ${qty(P)} ${uw(P)}`,
        rows: same.map((o) => {
          const v = liveValue(o);
          return { bin: o.display.binNumber, value: `${qty(v)} ${uw(v)}`, was: v !== o.current ? qty(o.current) : '', isThis: o.key === b.key };
        }),
        sumLabel: `${qty(sum)} of ${qty(P)} ${uw(P)}` + (over > 0 ? ` · ${qty(over)} over` : ''),
        over: over > 0,
      };
    }

    // The move control (PT-6b): offered on a row that has unposted units, when another line of the item exists.
    const unposted = Math.max(b.current - b.posted, 0);
    const anyTargetRoom = plannedLines.some((l) => l.id !== b.lineId && room(l.id) > 0);
    const serState = form.ser[b.key];
    const serMode: AdjustRowModel['serMode'] = kind === 'serial' ? serState?.mode ?? 'keep' : 'keep';
    const canMoveQty = kind === 'qty' && multi && b.lineId != null && b.slotId != null;
    const canMoveSer = kind === 'serial' && multi && b.lineId != null && b.slotId != null && anyTargetRoom && unposted > 0;
    // No "already posted" reason here: a row with no unposted units is locked, and shows no move control at all.
    const moveBlocked = dirty
      ? 'Apply or reset your changes first'
      : !anyTargetRoom ? 'No other MRS line of this item has room for more' : '';

    // Dispositions (C4; settlement rows 41–44): the card's question on its last row; a serial's on its own row.
    let disposition: DispositionModel | null = null;
    if (kind === 'serial' && serMode === 'return') {
      disposition = {
        question: `Where does ${b.serial} go?`,
        value: serState?.disp ?? null,
        target: 'serial',
        flagText: DISPOSITION_FLAG(b.display.binNumber),
        incomplete: serState?.disp == null,
      };
    } else if (!isSerial && lastKeyOfDisplay.get(b.display.id) === b.key) {
      const decrease = binState.get(b.display.id)?.decrease ?? 0;
      if (decrease > 0) {
        disposition = {
          question: `Where did the unassigned ${qty(decrease)} ${uw(decrease)} go?`,
          value: form.disp[b.display.id] ?? null,
          target: 'bin',
          flagText: DISPOSITION_FLAG(b.display.binNumber),
          incomplete: form.disp[b.display.id] == null,
        };
      }
    }

    return {
      key: b.key,
      lineId: b.lineId,
      slotId: b.slotId,
      displayBinId: b.display.id,
      kind,
      current: b.current,
      floor: b.posted,
      serial: b.serial,
      mrsNumber: multi && b.lineId != null ? lineById.get(b.lineId)?.mrsNumber ?? null : null,
      mrsSub: b.lineId != null && hasSiblingOnMrs(b.lineId) ? labelTail(label(b.lineId)) : '',
      plain: !b.serial && !(multi && b.lineId != null),
      curLabel: taken ? 'not picked yet' : `current ${qty(b.current)}`,
      postedNote: kind === 'qty' && b.posted > 0
        ? {
            open: `${qty(unposted)} can change`,
            posted: `${qty(b.posted)} posted`,
            tip: `${qty(b.posted)} ${uw(b.posted)} of this line are already posted to NetSuite. They can’t be lowered or moved here`
              + ` — the other ${qty(unposted)} can.`,
          }
        : null,
      planLabel,
      planTip,
      lockedLabel: b.current === 0 ? 'not picked' : 'fully posted',
      takenLabel: b.taken
        ? `Picked on ${b.taken.picklistNumber} (${b.taken.pickedAtLabel}) — swap it from the item’s bin card, or on the handheld.`
        : '',
      value: text(b),
      error,
      canMoveQty,
      canMoveSer,
      moveTip: moveBlocked || 'Move to another MRS line',
      moveDisabled: moveBlocked !== '',
      serMode,
      disposition,
      breakdown,
    };
  });

  // Cards: one per display bin; a serialized item's serial display bins group by live bin.
  const cards: AdjustCardModel[] = [];
  const cardOf = new Map<string, AdjustCardModel>();
  bases.forEach((b, i) => {
    const key = isSerial ? `live:${b.display.binId}` : `bin:${b.display.id}`;
    let card = cardOf.get(key);
    if (!card) {
      card = { key, bin: b.display.binNumber, meta: '', sum: isSerial ? null : binState.get(b.display.id)?.sum ?? null, rows: [], sig: '' };
      cardOf.set(key, card);
      cards.push(card);
    }
    card.rows.push(rowModels[i]);
  });
  for (const card of cards) {
    const displays = [...new Set(bases.filter((b) => (isSerial ? `live:${b.display.binId}` : `bin:${b.display.id}`) === card.key).map((b) => b.display))];
    const pk = displays.reduce((a, d) => a + d.currentlyPickedQuantity, 0);
    const posted = displays.reduce((a, d) => a + d.postedQuantity, 0);
    const lineRows = card.rows.filter((r) => r.lineId != null).length;
    card.meta = `picked ${qty(pk)} ${uw(pk)}`
      + (posted > 0 ? ` · ${qty(posted)} posted` : '')
      + (isSerial
        ? ` · ${card.rows.length} ${card.rows.length === 1 ? 'serial' : 'serials'}`
        : lineRows > 1 ? ` · ${lineRows} MRS lines` : '');
    card.sig = JSON.stringify({ ...card, sig: undefined });
  }

  // What Apply sends (C6, C8). Rows only: a Move is its own request (PT-6b).
  const plan: AdjustPlanBody = { rows: [], moves: [], dispositions: [] };
  let missing = 0;
  bases.forEach((b, i) => {
    const r = rowModels[i];
    if (b.lineId == null || b.slotId == null) return;
    if (r.kind === 'qty' && !r.error) {
      const n = num(b);
      if (Number.isFinite(n) && n !== b.current) {
        plan.rows.push({ picklistItemId: b.lineId, picklistItemBinId: b.slotId, newQuantity: n });
      }
    }
    if (r.kind === 'serial' && r.serMode === 'return') {
      plan.rows.push({ picklistItemId: b.lineId, picklistItemBinId: b.slotId, newQuantity: b.posted });
      const d = form.ser[b.key]?.disp;
      if (d != null) plan.dispositions.push({ picklistItemBinId: b.display.id, disposition: d });
      else missing += 1;
    }
  });
  if (!isSerial) {
    for (const [displayId, state] of binState) {
      if (state.decrease <= 0) continue;
      const d = form.disp[displayId];
      if (d != null) plan.dispositions.push({ picklistItemBinId: displayId, disposition: d });
      else missing += 1;
    }
  }

  const anyErr = rowModels.some((r) => r.error != null);
  const changed = plan.rows.length > 0;
  const needNote = form.reason === PicklistAdjustmentReason.Other && !form.notes.trim();

  const serEditable = rowModels.filter((r) => r.kind === 'serial').length;
  const retCount = rowModels.filter((r) => r.kind === 'serial' && r.serMode === 'return').length;

  return {
    code: group.itemCode,
    name: group.itemName,
    unit,
    isSerial,
    multi,
    cards,
    bannerTail: 'Lowered units leave the pick — choose below where they went. Only what’s left gets posted on the MIS.',
    hintText: multi && !isSerial
      ? 'A line can only go down here. To count units for another MRS line, use the move icon.'
      : 'Can’t exceed current or drop below the posted floor',
    showHint: serEditable <= 1,
    bulkShow: serEditable > 1,
    bulkSummary: retCount > 0 ? `${retCount} of ${serEditable} to unpick` : '',
    returnAllKeys: rowModels.filter((r) => r.kind === 'serial').map((r) => r.key),
    qtyReset: !isSerial && (Object.keys(form.vals).length > 0 || Object.keys(form.disp).length > 0),
    needNote,
    canApply: changed && !anyErr && !needNote && missing === 0,
    plan,
  };
}

/** The group a page target id names: its anchor, or any of its line ids (PicklistItemGroups.FindGroup). */
export function findGroup(groups: readonly PicklistItemGroupDto[] | undefined, id: number | null): PicklistItemGroupDto | null {
  if (id == null) return null;
  return (groups ?? []).find((g) => g.id === id || (g.plannedLines ?? []).some((l) => l.id === id)) ?? null;
}
